<!--
  @deprecated 旧料金体系（timeSlotId を持たない体験イベント）向けの料金表示。
  呼び出し元の ReservationStep1Form では `!timeSlotId && !IS_NEW_PRICE_PLAN_ENABLED` の場合のみ
  描画されるため、新料金体系では使用されない。
  新規の料金表示は common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue を使用すること。
-->
<template>
  <section>
    <div class="mb-4 bg-beige1 px-6 py-6 md:px-10">
      <div class="pb-8">
        <p class="mb-2 text-xs font-bold md:text-[13px]">Experience fee</p>
        <div class="flex justify-between text-sm md:text-[17px]">
          <p v-if="isGroup" data-testid="groupFee">Group fee ({{ props.numberOfParticipants }} participants)</p>
          <p v-else data-testid="numberOfParticipantsFee">
            Number of Participants fee
            <template v-if="!hasPreReservationPrice"><br />JPY {{ baseInfo.eventPriceIncludingTax }} × {{ props.numberOfParticipants }}</template>
          </p>
          <p class="whitespace-nowrap" data-testid="experienceFee">JPY {{ prices.experienceFee.toLocaleString() }}</p>
        </div>
        <div v-if="!isEventSelected">
          <p class="text-gray2">Price may change after date is selected.</p>
        </div>
      </div>
      <div class="pb-8">
        <p class="mb-2 text-xs font-bold md:text-[13px]">Additional options fee</p>
        <div class="flex justify-between text-sm leading-7 md:text-[17px]">
          <p>Interpretation in {{ selectedInterpreterOption.name }}</p>
          <p class="whitespace-nowrap" data-testid="additionalInterpreterOptionFee">JPY {{ selectedInterpreterOption.unitPriceIncludingTax }}</p>
        </div>
        <div v-for="(option, index) in selectedExperienceEventOptions" :key="index" class="tracking- mt-2 flex justify-between text-sm leading-7 md:text-[17px]">
          <p>{{ option.name }} × {{ option.count }}</p>
          <p class="whitespace-nowrap" data-testid="additionalExperienceOptionFee">JPY {{ option.totalPriceIncludingTaxString }}</p>
        </div>
      </div>
      <div class="pb-2">
        <p class="mb-2 text-xs font-bold md:text-[13px]">Other</p>
        <div class="flex justify-between pb-2 text-base leading-7 md:text-[17px]">
          <p>Service fee (5%)</p>
          <p class="whitespace-nowrap" data-testid="serviceFee">JPY {{ prices.serviceFee.toLocaleString() }}</p>
        </div>
        <div
          v-if="appliedCode && appliedCode.benefitType && appliedCode.benefitType == 'DISCOUNT_PERCENTAGE' && appliedCode.benefitValue != null && appliedCode.benefitValue != 0"
          class="flex justify-between pb-2 text-base leading-7 md:text-[17px]"
        >
          <p>Discount ({{ appliedCode.benefitValue }}%)</p>
          <p class="whitespace-nowrap" data-testid="discountAmount">- JPY {{ prices.discountAmount.toLocaleString() }}</p>
        </div>
        <div
          v-if="appliedCode && appliedCode.benefitType && appliedCode.benefitType == 'DISCOUNT_AMOUNT' && appliedCode.benefitValue != null && appliedCode.benefitValue != 0"
          class="flex justify-between pb-2 text-base leading-7 md:text-[17px]"
        >
          <p>Discount (- JPY {{ appliedCode.benefitValue.toLocaleString() }})</p>
          <p class="whitespace-nowrap" data-testid="discountAmount">- JPY {{ prices.discountAmount.toLocaleString() }}</p>
        </div>
      </div>
      <AtomsBorder1 class="mb-3 bg-gray4" />
      <div class="flex flex-wrap justify-end pt-4 text-base md:text-[17px]">
        <p class="mr-auto leading-8">Subtotal(JPY)</p>
        <p class="text-[26px] md:mr-2 md:font-normal md:tracking-normal" data-testid="totalPrice">JPY {{ prices.totalPriceIncludingTax.toLocaleString() }}</p>
      </div>
      <p class="mt-3 text-right text-[11px] leading-4 md:text-[13px]">tax & service fee incl.</p>
      <p v-if="props.preReservationPrice?.currencyConvertedPrice" class="mt-1 text-right text-[11px] leading-4 text-gray2 md:text-[13px]">
        Approx. {{ props.preReservationPrice.currencyConvertedPrice.formattedAmount }}
      </p>
      <p v-if="props.preReservationPrice?.currencyConvertedPrice" class="mt-2 text-[11px] leading-4 text-gray2 md:text-[13px]">
        * Payments and refunds are processed in Japanese Yen (JPY). The displayed converted price is for reference only and may vary with exchange rates.
      </p>
      <p class="mr-auto mt-4 text-base leading-8 md:text-[17px]">*Prices may vary depending on options selected.</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { calculateDefaultExperiencePrices, type ExperiencePreReservationPriceViewModel, type WabunkaCode } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';

interface Props {
  defaultExperience?: WabunkaExperienceViewModel;
  selectedExperienceEvent: WabunkaExperienceEventViewModel;
  numberOfParticipants: number;
  appliedCode?: WabunkaCode;
  preReservationPrice?: ExperiencePreReservationPriceViewModel;
}

const props = defineProps<Props>();

const isEventSelected = computed(() => props.selectedExperienceEvent.id !== '');
const baseInfo = computed(() => {
  const eventPriceIncludingTax = isEventSelected.value ? props.selectedExperienceEvent.priceIncludingTax : (props.defaultExperience?.priceIncludingTax ?? 0);

  return {
    eventPriceIncludingTax,
  };
});
const isGroup = computed<boolean>(() => (props.defaultExperience != null ? props.defaultExperience.chargeType === 'PER_GROUP' : props.selectedExperienceEvent.experience.chargeType === 'PER_GROUP'));

// 料金計算 API の結果が使えるか。体験イベント未選択時は空の ViewModel が渡るため、
// オブジェクトの有無ではなく金額が入っているかで判定する（下の prices のフォールバック条件と揃える）。
const hasPreReservationPrice = computed<boolean>(() => props.preReservationPrice != null && props.preReservationPrice.totalPriceIncludingTaxString !== '');

const prices = computed(() =>
  hasPreReservationPrice.value && props.preReservationPrice
    ? {
        experienceFee: props.preReservationPrice.totalExperienceEventPriceIncludingTaxString,
        serviceFee: props.preReservationPrice.serviceFeeIncludingTaxString,
        discountAmount: props.preReservationPrice.discountAmountString ?? 0,
        totalPriceIncludingTax: props.preReservationPrice.totalPriceIncludingTaxString,
      }
    : // 料金計算結果が空、つまり体験イベントが選択されていない場合は体験マスタの情報を返却する
      calculateDefaultExperiencePrices(baseInfo.value.eventPriceIncludingTax, props.numberOfParticipants, isGroup.value, props.defaultExperience?.interpreterOptions[0].priceIncludingTax ?? 0),
);

const selectedExperienceEventOptions = computed(() => props.preReservationPrice?.experienceEventOptionPriceSets.filter((option) => option.count > 0));

const selectedInterpreterOption = computed(() => {
  if (props.preReservationPrice) {
    const option = props.preReservationPrice.interpreterOptionPriceSets.find((option) => option.count === 1);
    return {
      name: option?.name ?? '',
      unitPriceIncludingTax: option?.unitPriceIncludingTaxString ?? '0',
    };
  }
  // 料金計算結果が空、つまり体験イベントが選択されていない場合は体験マスタの情報を返却する
  return {
    name: props.defaultExperience?.interpreterOptions[0]?.name ?? '',
    unitPriceIncludingTax: props.defaultExperience?.interpreterOptions[0].priceIncludingTax.toLocaleString() ?? '0',
  };
});
</script>
